import { useState, useEffect, useMemo, useRef } from 'react';
import { supabase, OperationType, handleSupabaseError } from './supabase';
import { CryptoEvent, UserProfile, rowToEvent, rowToProfile } from './types';
import { Calendar } from './components/Calendar';
import { EventDetailModal } from './components/EventDetailModal';
import { AddEventModal } from './components/AddEventModal';
import { AdminPanel } from './components/AdminPanel';
import { ClientDashboard } from './components/ClientDashboard';
import { MyEvents } from './components/MyEvents';
import { AccountSettings } from './components/AccountSettings';
import { Auth } from './components/Auth';
import { EventDetailPage } from './pages/EventDetailPage';
import {
  Calendar as CalendarIcon,
  LogOut,
  Shield,
  LayoutDashboard,
  Clock,
  AlertCircle,
  Settings,
  ChevronDown,
  User,
  Twitter,
  CheckCircle2,
  X,
  Trash2,
  Plus,
} from 'lucide-react';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Renders during /auth/callback. Waits for Supabase to detect the session
 * from the URL hash, then navigates home. Surfaces any OAuth error in the URL.
 */
function AuthCallbackHandler({ onDone }: { onDone: () => void }) {
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Surface OAuth provider errors that come back as URL params.
    const hashParams = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const queryParams = new URLSearchParams(window.location.search);
    const oauthError =
      hashParams.get('error_description') ||
      hashParams.get('error') ||
      queryParams.get('error_description') ||
      queryParams.get('error');
    if (oauthError) {
      setError(decodeURIComponent(oauthError.replace(/\+/g, ' ')));
      return;
    }

    // detectSessionInUrl already runs on client init; wait briefly then navigate.
    let cancelled = false;
    const finish = async () => {
      // Try a few times — Supabase processes the hash shortly after mount.
      for (let i = 0; i < 20; i++) {
        const { data: { session } } = await supabase.auth.getSession();
        if (cancelled) return;
        if (session) {
          // Clean the URL of any tokens before navigating
          window.history.replaceState({}, '', window.location.pathname);
          onDone();
          return;
        }
        await new Promise((r) => setTimeout(r, 250));
      }
      if (!cancelled) {
        setError('Sign-in did not complete. Please close this tab and try again.');
      }
    };
    void finish();
    return () => { cancelled = true; };
  }, [onDone]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#f3ecfc] p-4">
      <div className="sticker bg-white p-6 max-w-md w-full text-center">
        {error ? (
          <>
            <div className="w-12 h-12 sticker-soft bg-[#ff6a6a] inline-flex items-center justify-center mb-3">
              <AlertCircle className="w-6 h-6 text-white" />
            </div>
            <p className="font-bold text-lg uppercase tracking-tight mb-2">Sign-in failed</p>
            <p className="text-sm text-[#0b0b0c]/65 mb-4">{error}</p>
            <a
              href="/"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-[#9945ff] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider"
            >
              Back to calendar
            </a>
          </>
        ) : (
          <>
            <div className="w-12 h-12 border-4 border-[#9945ff] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
            <p className="font-bold text-lg uppercase tracking-tight">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  );
}

type Route =
  | { name: 'home' }
  | { name: 'event'; id: string }
  | { name: 'auth-callback' };

const NAV_LINKS = [
  { href: 'https://onsolana.link/', label: 'Map' },
  { href: 'https://start.onsolana.link/', label: 'Get Started' },
  { href: 'https://news.onsolana.link/', label: 'News' },
  { href: 'https://calendar.onsolana.link/', label: 'Calendar', active: true },
  { href: 'https://lore.onsolana.link/', label: 'Lore' },
];

function parseRoute(): Route {
  const path = window.location.pathname;
  if (path.startsWith('/event/')) {
    const id = path.slice('/event/'.length).replace(/\/$/, '');
    if (id) return { name: 'event', id };
  }
  if (path === '/auth/callback') return { name: 'auth-callback' };
  return { name: 'home' };
}

export default function App() {
  const [route, setRoute] = useState<Route>(parseRoute);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<CryptoEvent[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<CryptoEvent | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showClientDashboard, setShowClientDashboard] = useState(false);
  const [showMyEvents, setShowMyEvents] = useState(false);
  const [showAccountSettings, setShowAccountSettings] = useState(false);
  const [showUserDropdown, setShowUserDropdown] = useState(false);
  const [usersMap, setUsersMap] = useState<Record<string, UserProfile>>({});
  const [authError, setAuthError] = useState<string | null>(null);
  const [sessionStatus, setSessionStatus] =
    useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [editingEvent, setEditingEvent] = useState<CryptoEvent | null>(null);
  const [deletingEventId, setDeletingEventId] = useState<string | null>(null);
  const [submittingChange, setSubmittingChange] = useState(false);
  const [notification, setNotification] =
    useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [forceCalendarView, setForceCalendarView] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const navShellRef = useRef<HTMLDivElement | null>(null);

  // ----- routing ------------------------------------------------
  useEffect(() => {
    const onPop = () => setRoute(parseRoute());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = (to: string) => {
    if (window.location.pathname !== to) {
      window.history.pushState({}, '', to);
      setRoute(parseRoute());
      window.scrollTo({ top: 0 });
    }
  };

  // ----- session status from URL ---------------------------------
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('deleted') === 'true') {
      setSessionStatus({ type: 'success', message: 'Your account has been successfully deleted.' });
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, []);

  useEffect(() => {
    if (sessionStatus) {
      const timer = setTimeout(() => setSessionStatus(null), 6000);
      return () => clearTimeout(timer);
    }
  }, [sessionStatus]);

  // ----- auth + profile subscription -----------------------------
  useEffect(() => {
    let profileChannel: ReturnType<typeof supabase.channel> | null = null;

    const loadProfile = async (userId: string) => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .maybeSingle();
      if (error) {
        setAuthError(`Profile fetch error: ${error.message}`);
        setUser(null);
      } else if (data) {
        setUser(rowToProfile(data));
        setAuthError(null);
      } else {
        setUser(null);
      }
      setLoading(false);
    };

    const subscribeProfile = (userId: string) => {
      if (profileChannel) supabase.removeChannel(profileChannel);
      profileChannel = supabase
        .channel(`profile:${userId}`)
        .on(
          'postgres_changes',
          { event: '*', schema: 'public', table: 'profiles', filter: `id=eq.${userId}` },
          () => void loadProfile(userId),
        )
        .subscribe();
    };

    const init = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        await loadProfile(session.user.id);
        subscribeProfile(session.user.id);
      } else {
        setUser(null);
        setLoading(false);
      }
    };
    void init();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session?.user) {
        void loadProfile(session.user.id);
        subscribeProfile(session.user.id);
      } else {
        if (profileChannel) {
          supabase.removeChannel(profileChannel);
          profileChannel = null;
        }
        setUser(null);
        setShowAdmin(false);
        setShowClientDashboard(false);
        setShowMyEvents(false);
        setShowAccountSettings(false);
        setForceCalendarView(false);
        setLoading(false);
        setAuthError(null);
      }
    });

    return () => {
      sub.subscription.unsubscribe();
      if (profileChannel) supabase.removeChannel(profileChannel);
    };
  }, []);

  // ----- approved events subscription ---------------------------
  useEffect(() => {
    const fetchApprovedEvents = async () => {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .eq('status', 'approved')
        .order('starts_at', { ascending: true });
      if (error) {
        handleSupabaseError(error, OperationType.SELECT, 'events');
        return;
      }
      setEvents((data ?? []).map(rowToEvent));
    };
    void fetchApprovedEvents();

    const channel = supabase
      .channel('events:approved')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'events' },
        () => void fetchApprovedEvents(),
      )
      .subscribe();

    return () => void supabase.removeChannel(channel);
  }, []);

  // ----- hydrate users map for event submitters ----------------
  useEffect(() => {
    if (!user) return;
    const uids = Array.from(new Set(events.map((e) => e.createdBy).filter((v): v is string => !!v)));
    const missing = uids.filter((uid) => !usersMap[uid]);
    if (missing.length === 0) return;
    (async () => {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .in('id', missing);
      if (error || !data) return;
      setUsersMap((prev) => {
        const next = { ...prev };
        for (const row of data) next[row.id] = rowToProfile(row);
        return next;
      });
    })();
  }, [events, user, usersMap]);

  const handleLogout = () => supabase.auth.signOut();

  const handleAddEventClick = () => {
    if (!user) {
      window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' });
    } else if (user.role === 'client' && !user.isApproved) {
      setForceCalendarView(false);
    } else {
      setShowAddModal(true);
    }
  };

  const handleDelete = async (eventId: string) => {
    setSubmittingChange(true);
    try {
      const { error } = await supabase.from('events').delete().eq('id', eventId);
      if (error) throw error;
      setNotification({ type: 'success', message: 'Event deleted successfully.' });
      setDeletingEventId(null);
      if (selectedEvent?.id === eventId) setSelectedEvent(null);
    } catch (error) {
      setNotification({ type: 'error', message: 'Failed to delete event.' });
      handleSupabaseError(error, OperationType.DELETE, 'events');
    } finally {
      setSubmittingChange(false);
    }
  };

  const handleHomeClick = () => {
    setShowAdmin(false);
    setShowClientDashboard(false);
    setShowMyEvents(false);
    setShowAccountSettings(false);
    setForceCalendarView(true);
    setShowUserDropdown(false);
    setSelectedEvent(null);
    navigate('/');
  };

  useEffect(() => {
    if (!mobileNavOpen) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setMobileNavOpen(false);
    };
    const onPointerDown = (event: PointerEvent) => {
      if (!navShellRef.current?.contains(event.target as Node)) {
        setMobileNavOpen(false);
      }
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [mobileNavOpen]);

  const eventCount = useMemo(() => events.length, [events]);

  if (route.name === 'auth-callback') {
    return <AuthCallbackHandler onDone={() => navigate('/')} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#f3ecfc] gap-4">
        <img src="/logo.svg" alt="What's On" className="h-14 w-auto" />
        <div className="w-10 h-10 border-4 border-[#9945ff] border-t-transparent rounded-full animate-spin" />
        <p className="text-sm font-medium text-[#0b0b0c]/55">Initializing...</p>
      </div>
    );
  }

  if (route.name === 'event') {
    return <EventDetailPage eventId={route.id} onBack={() => navigate('/')} />;
  }

  return (
    <div className="min-h-screen bg-[#f3ecfc] text-[#0b0b0c] flex flex-col">
      {/* Nav — black pill, mirrors the onsolana.link main-site nav. */}
      <div ref={navShellRef} className="sticky top-0 z-40 px-3 sm:px-4 pt-[calc(env(safe-area-inset-top)+0.75rem)] sm:pt-4 flex flex-col sm:flex-row items-center justify-center gap-3">
        <nav
          className="site-nav inline-flex items-center rounded-full bg-[#0b0b0c] text-white max-w-full overflow-x-auto"
          style={{ gap: 6, padding: 8, boxShadow: '4px 4px 0 0 rgba(0,0,0,0.3)' }}
        >
          {/* Logo / wordmark — links back to onsolana.link */}
          <a
            href="https://onsolana.link/"
            aria-label="onsolana.link home"
            className="flex shrink-0 items-center"
            style={{
              padding: '0 8px',
              fontFamily: "'Rebook', 'Outfit', system-ui, sans-serif",
              fontSize: '1.25rem',
              letterSpacing: '0.025em',
              lineHeight: 1,
              color: '#fff',
              whiteSpace: 'nowrap',
            }}
          >
            onsolana.link
          </a>

          {/* Divider */}
          <span
            aria-hidden="true"
            style={{
              width: 1,
              height: 24,
              background: 'rgba(255,255,255,0.2)',
              borderRadius: 9999,
              margin: '0 4px',
            }}
          />

          {/* Page links — point back to onsolana.link, except CALENDAR which is here */}
          <div className="site-nav-links hidden sm:inline-flex items-center" style={{ gap: 6 }}>
            {NAV_LINKS.map(({ href, label, active }) => (
              <a
                key={label}
                href={href}
                onClick={
                  label === 'Calendar'
                    ? (e) => {
                        e.preventDefault();
                        handleHomeClick();
                      }
                    : undefined
                }
                style={{
                  fontFamily: "'Rebook', 'Outfit', system-ui, sans-serif",
                  fontSize: 13,
                  letterSpacing: '0.05em',
                  textTransform: 'uppercase',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  padding: '8px 16px',
                  borderRadius: 9999,
                  color: active ? '#0b0b0c' : 'rgba(255,255,255,0.75)',
                  background: active ? '#ffffff' : 'transparent',
                  transition: 'color 150ms, background-color 150ms',
                }}
                aria-current={active ? 'page' : undefined}
              >
                {label}
              </a>
            ))}
          </div>

          {/* Right divider */}
          <span
            aria-hidden="true"
            className="hidden sm:inline-block"
            style={{
              width: 1,
              height: 24,
              background: 'rgba(255,255,255,0.2)',
              borderRadius: 9999,
              margin: '0 4px',
            }}
          />

          {/* "What's on?" sticker badge — clicking goes home (this calendar). */}
          <button
            onClick={handleHomeClick}
            aria-label="What's On, home"
            className="site-nav-badge inline-flex items-center min-h-11"
            style={{
              gap: 10,
              padding: '0 6px 0 4px',
              lineHeight: 1,
              background: 'transparent',
              border: 0,
              cursor: 'pointer',
            }}
          >
            <span
              className="font-display"
              style={{
                fontSize: 18,
                letterSpacing: '-0.005em',
                color: '#fff',
                textShadow: '2px 2.5px 0 #522A83',
              }}
            >
              {['W', 'h', 'a', 't', '’', 's', ' ', 'o', 'n', '?'].map(
                (ch, i) => (
                  <span
                    key={i}
                    style={{
                      display: 'inline-block',
                      transform: `rotate(${i % 2 === 0 ? -3 : 3}deg)`,
                    }}
                  >
                    {ch}
                  </span>
                ),
              )}
            </span>
            <span
              aria-hidden="true"
              className="grid place-items-center shrink-0"
              style={{
                width: 32,
                height: 32,
                borderRadius: 8,
                background: '#9B5CFF',
                boxShadow: '2px 2.5px 0 #522A83',
                transform: 'rotate(-4deg)',
              }}
            >
              <CalendarIcon
                className="text-white"
                style={{ width: 18, height: 18 }}
                strokeWidth={2.4}
              />
            </span>
          </button>

          <button
            type="button"
            className={`mobile-menu-button${mobileNavOpen ? ' is-open' : ''}`}
            aria-label={mobileNavOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileNavOpen}
            aria-controls="calendar-mobile-menu"
            onClick={() => setMobileNavOpen((open) => !open)}
          >
            {mobileNavOpen ? (
              <svg className="mobile-menu-x-icon" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M5 5l14 14M19 5L5 19" fill="none" stroke="currentColor" />
              </svg>
            ) : (
              <>
                <span />
                <span />
                <span />
              </>
            )}
          </button>
        </nav>

        {mobileNavOpen && (
          <div id="calendar-mobile-menu" className="mobile-menu-panel">
            {NAV_LINKS.map(({ href, label, active }) => (
              <a
                key={label}
                className={`mobile-menu-link${active ? ' is-active' : ''}`}
                href={href}
                aria-current={active ? 'page' : undefined}
                onClick={(event) => {
                  setMobileNavOpen(false);
                  if (label === 'Calendar') {
                    event.preventDefault();
                    handleHomeClick();
                  }
                }}
              >
                {label.toUpperCase()}
              </a>
            ))}
          </div>
        )}

        {/* Floating user controls — top-right of viewport, separate from the pill. */}
        <div className="static sm:absolute sm:right-4 sm:top-4 flex items-center justify-end gap-2 max-w-full">
            {user && user.role === 'admin' && (
              <button
                onClick={() => {
                  setShowAdmin(!showAdmin);
                  setShowClientDashboard(false);
                  setShowMyEvents(false);
                  setShowAccountSettings(false);
                }}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 min-h-11 sm:min-h-9 rounded-full border-[1.5px] border-[#0b0b0c] text-[11px] font-bold uppercase tracking-wider transition-colors',
                  showAdmin ? 'bg-[#0b0b0c] text-white' : 'bg-white text-[#0b0b0c]',
                )}
              >
                {showAdmin ? <CalendarIcon className="w-3.5 h-3.5" /> : <Shield className="w-3.5 h-3.5" />}
                <span className="hidden sm:inline">{showAdmin ? 'Calendar' : 'Admin'}</span>
              </button>
            )}
            {user && user.role === 'client' && user.isApproved && (
              <button
                onClick={() => {
                  setShowClientDashboard(!showClientDashboard);
                  setShowAdmin(false);
                  setShowMyEvents(false);
                  setShowAccountSettings(false);
                }}
                className={cn(
                  'inline-flex items-center gap-1.5 px-3 min-h-11 sm:min-h-9 rounded-full border-[1.5px] border-[#0b0b0c] text-[11px] font-bold uppercase tracking-wider transition-colors',
                  showClientDashboard ? 'bg-[#0b0b0c] text-white' : 'bg-white text-[#0b0b0c]',
                )}
              >
                <LayoutDashboard className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Dashboard</span>
              </button>
            )}

            {user ? (
              <div className="relative">
                <button
                  onClick={() => setShowUserDropdown(!showUserDropdown)}
                  className="inline-flex items-center gap-1.5 px-2 min-h-11 sm:min-h-9 rounded-full border-[1.5px] border-[#0b0b0c] bg-white text-[#0b0b0c] hover:bg-[#0b0b0c] hover:text-white transition-colors"
                >
                  <span className="w-6 h-6 rounded-full bg-[#9945ff] text-white inline-flex items-center justify-center">
                    {user.authProvider === 'twitter' ? (
                      <Twitter className="w-3.5 h-3.5" />
                    ) : (
                      <User className="w-3.5 h-3.5" />
                    )}
                  </span>
                  <span className="hidden md:inline text-[11px] font-bold uppercase tracking-wider truncate max-w-[120px]">
                    {user.displayName || user.companyName}
                  </span>
                  <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', showUserDropdown && 'rotate-180')} />
                </button>

                {showUserDropdown && (
                  <>
                    <button
                      type="button"
                      aria-label="Close user menu"
                      className="fixed inset-0 z-40 cursor-default"
                      onClick={() => setShowUserDropdown(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-[min(14rem,calc(100vw-2rem))] sticker bg-white py-2 z-50">
                      <button
                        onClick={() => {
                          setShowMyEvents(true);
                          setShowAdmin(false);
                          setShowClientDashboard(false);
                          setShowAccountSettings(false);
                          setShowUserDropdown(false);
                        }}
                        className="w-full min-h-11 flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-[#0b0b0c] hover:bg-[#f3ecfc] transition-colors"
                      >
                        <Clock className="w-4 h-4" />
                        My events
                      </button>
                      <button
                        onClick={() => {
                          setShowAccountSettings(true);
                          setShowAdmin(false);
                          setShowClientDashboard(false);
                          setShowMyEvents(false);
                          setShowUserDropdown(false);
                        }}
                        className="w-full min-h-11 flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-[#0b0b0c] hover:bg-[#f3ecfc] transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        Account settings
                      </button>
                      <div className="h-px bg-[#0b0b0c]/10 my-1 mx-2" />
                      <button
                        onClick={() => {
                          handleLogout();
                          setShowUserDropdown(false);
                        }}
                        className="w-full min-h-11 flex items-center gap-3 px-4 py-2.5 text-sm font-bold text-[#ff6b4d] hover:bg-[#ff6b4d]/10 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign out
                      </button>
                    </div>
                  </>
                )}
              </div>
            ) : null}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-3 sm:px-6 pt-8 sm:pt-12 pb-16 flex-1 w-full">
        {sessionStatus && (
          <div className="max-w-md mx-auto mb-6 sticker-soft bg-white p-3 flex items-start gap-3">
            {sessionStatus.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 text-[#14f195] shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-[#ff6a6a] shrink-0 mt-0.5" />
            )}
            <p className="text-sm font-medium text-[#0b0b0c] flex-1">{sessionStatus.message}</p>
            <button
              onClick={() => setSessionStatus(null)}
              className="w-10 h-10 -mr-2 -mt-2 inline-flex items-center justify-center hover:bg-[#0b0b0c]/5 rounded-full"
              aria-label="Dismiss status"
            >
              <X className="w-4 h-4 text-[#0b0b0c]/55" />
            </button>
          </div>
        )}

        {authError && (
          <div className="max-w-md mx-auto mb-6 sticker-soft bg-[#ff6a6a] text-white p-3 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-bold mb-0.5">Configuration error</p>
              <p className="text-[12px]">{authError}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-2 text-[10px] font-bold uppercase tracking-wider hover:underline"
              >
                Retry
              </button>
            </div>
          </div>
        )}

        {user && user.role === 'client' && !user.isApproved && !forceCalendarView ? (
          <div className="max-w-xl mx-auto py-12">
            <div className="sticker bg-[#e9dffa] p-5 sm:p-8 text-center">
              <div className="w-16 h-16 sticker-soft bg-[#ff6b4d] inline-flex items-center justify-center mb-5">
                <Clock className="w-8 h-8 text-white" />
              </div>
              <p className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55">
                Pending approval
              </p>
              <h2 className="font-bold text-3xl sm:text-4xl uppercase tracking-tight text-[#0b0b0c] mt-1 mb-4">
                Hang tight
              </h2>
              <div className="sticker-soft bg-white p-4 mb-6 text-left">
                <p className="font-bold text-[10px] uppercase tracking-wider text-[#0b0b0c]/55 mb-1">
                  Authenticated as
                </p>
                <p className="text-sm font-bold text-[#0b0b0c]">{user.displayName || user.companyName}</p>
                <p className="text-xs text-[#0b0b0c]/55 mt-0.5 break-words">
                  {user.companyName} · {user.email || (user.twitterHandle ? `@${user.twitterHandle}` : 'No email')}
                </p>
              </div>
              <p className="text-sm text-[#0b0b0c]/65 mb-6 leading-relaxed">
                Your account is under review. An admin will approve your access shortly. You'll get full submission tools once approved.
              </p>
              <div className="flex flex-col gap-2">
                <button
                  onClick={() => setForceCalendarView(true)}
                  className="w-full h-12 rounded-full bg-white text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#0b0b0c] hover:text-white transition-colors inline-flex items-center justify-center gap-2"
                >
                  <CalendarIcon className="w-4 h-4" />
                  Back to calendar
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full h-11 rounded-full bg-[#0b0b0c] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:opacity-90 transition-opacity inline-flex items-center justify-center gap-2"
                >
                  <LogOut className="w-4 h-4" />
                  Logout
                </button>
              </div>
            </div>
          </div>
        ) : showAdmin && user?.role === 'admin' ? (
          <AdminPanel />
        ) : showClientDashboard && user?.role === 'client' && user.isApproved ? (
          <ClientDashboard
            userProfile={user}
            onAddEvent={() => setShowAddModal(true)}
            onSelectEvent={setSelectedEvent}
          />
        ) : showMyEvents && user ? (
          <MyEvents userProfile={user} isAdmin={user.role === 'admin'} />
        ) : showAccountSettings && user ? (
          <AccountSettings user={user} onBack={() => setShowAccountSettings(false)} />
        ) : (
          <div>
            <div className="text-center mb-10 sm:mb-16">
              <div className="inline-flex items-center gap-1.5 mb-4 px-3 h-7 pill-outline text-[11px] font-bold uppercase tracking-wider">
                <span className="w-2 h-2 rounded-full bg-[#14f195] animate-pulse" />
                {eventCount} {eventCount === 1 ? 'event' : 'events'} live
              </div>
              <h1
                className="font-display text-6xl sm:text-9xl md:text-[10rem] uppercase tracking-tight text-[#0b0b0c] leading-[0.9]"
                style={{ textShadow: '5px 5px 0 #9945ff' }}
              >
                Calendar
              </h1>
              <p className="mt-5 max-w-2xl mx-auto text-base sm:text-lg text-[#0b0b0c]/65 leading-relaxed">
                The most accurate calendar for Solana airdrops, hackathons, conferences, and major industry events. Curated by experts, submitted by builders.
              </p>
              {user && (
                <div className="mt-6 flex justify-center">
                  <button
                    onClick={handleAddEventClick}
                    className="inline-flex items-center gap-2 px-6 h-12 rounded-full bg-[#9945ff] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#5b29a6] transition-colors"
                  >
                    <Plus className="w-4 h-4" />
                    Submit event
                  </button>
                </div>
              )}
            </div>

            <Calendar
              events={events}
              user={user}
              onAddEvent={handleAddEventClick}
              onSelectEvent={setSelectedEvent}
            />
          </div>
        )}

        {!user && !showAdmin && !showClientDashboard && (
          <div className="mt-16 sm:mt-24 pt-12 border-t-[1.5px] border-[#0b0b0c]/15">
            <Auth />
          </div>
        )}
      </main>

      {selectedEvent && (
        <EventDetailModal
          event={selectedEvent}
          submitter={selectedEvent.createdBy ? usersMap[selectedEvent.createdBy] : undefined}
          onClose={() => setSelectedEvent(null)}
          isAdmin={user?.role === 'admin'}
          currentUserUid={user?.uid}
          onEdit={(event) => {
            setEditingEvent(event);
            setSelectedEvent(null);
          }}
          onDelete={(eventId) => setDeletingEventId(eventId)}
        />
      )}

      {notification && (
        <div className="fixed bottom-[max(2rem,env(safe-area-inset-bottom))] left-1/2 -translate-x-1/2 z-[100] w-full max-w-md px-4">
          <div
            className={cn(
              'sticker p-3 flex items-center justify-between gap-3',
              notification.type === 'success' ? 'bg-[#14f195]' : 'bg-[#ff6a6a] text-white',
            )}
          >
            <div className="flex items-center gap-2">
              {notification.type === 'success' ? (
                <CheckCircle2 className="w-5 h-5 text-[#0b0b0c]" />
              ) : (
                <AlertCircle className="w-5 h-5 text-white" />
              )}
              <span className="text-sm font-bold">{notification.message}</span>
            </div>
            <button
              onClick={() => setNotification(null)}
              className="text-xs font-bold uppercase tracking-wider hover:underline"
            >
              Dismiss
            </button>
          </div>
        </div>
      )}

      {deletingEventId && (
        <div className="fixed inset-0 z-[110] flex items-end sm:items-center justify-center safe-overlay bg-[#0b0b0c]/40">
          <div className="sticker bg-[#e9dffa] max-w-sm w-full p-6 text-center">
            <div className="w-14 h-14 sticker-soft bg-[#ff6b4d] inline-flex items-center justify-center mb-4">
              <Trash2 className="w-7 h-7 text-white" />
            </div>
            <h3 className="font-bold text-2xl uppercase tracking-tight text-[#0b0b0c] mb-1">
              Delete event?
            </h3>
            <p className="text-sm text-[#0b0b0c]/65 mb-5">
              This action cannot be undone. The event will be permanently removed.
            </p>
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                disabled={submittingChange}
                onClick={() => setDeletingEventId(null)}
                className="flex-1 h-11 rounded-full bg-white text-[#0b0b0c] border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#0b0b0c] hover:text-white transition-colors"
              >
                Cancel
              </button>
              <button
                disabled={submittingChange}
                onClick={() => handleDelete(deletingEventId)}
                className="flex-1 h-11 rounded-full bg-[#ff6b4d] text-white border-[1.5px] border-[#0b0b0c] font-bold text-sm uppercase tracking-wider hover:bg-[#ff6a6a] transition-colors disabled:opacity-50"
              >
                {submittingChange ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {(showAddModal || editingEvent) && user && (
        <AddEventModal
          onClose={() => {
            setShowAddModal(false);
            setEditingEvent(null);
          }}
          onSuccess={(message) => {
            if (editingEvent) {
              setEditingEvent(null);
              setNotification({ type: 'success', message });
            } else {
              setNotification({ type: 'success', message });
            }
          }}
          isAdmin={user.role === 'admin'}
          userProfile={user}
          editEvent={editingEvent || undefined}
        />
      )}

      <footer className="max-w-7xl mx-auto px-6 py-8 text-center text-[#0b0b0c]/55 text-xs font-medium border-t-[1.5px] border-[#0b0b0c]/15 w-full">
        <p>Where everybody goes to find out what's happening... on Solana.</p>
      </footer>
    </div>
  );
}
